import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// styles
import './style.scss';

// antd component
import { Form, Button, Upload, message } from 'antd';

// components
import CustomSelect from 'components/Forms/Fields/CustomSelect';
import TextareaField from 'components/Forms/Fields/TextareaField';
import TextField from 'components/Forms/Fields/TextField';
import DynamicMultipleSelect from 'components/Forms/Fields/DynamicMultipleSelect';
// media
import UploadMedia from 'mobile/containers/UploadMedia';
import PreviewMedia from 'components/Forms/Fields/PreviewMedia';

// utils
import { PARENT_COMM_REASON } from 'utils/helpers/helper.constant';
import { sortByKey, clean } from 'utils/helpers/helper.general';
import { normFile } from 'utils/uploadHelper';
// hooks
import { useRefState } from 'utils/hooks';

function RequestCommForm({
  loading,
  successful,
  formData,
  type,
  // actions
  nextStep,
  prevStep,
  result,
  setSelectedStudent,
  // default
  className,
}) {
  const childClassName = classNames('form-communication', className);

  const [isDisabled, setIsDisabled] = useState(true);
  const [CommForm] = Form.useForm();

  const [newAudiences, setNewAudiences] = useState({
    users: [],
  });

  // for media
  const [mediaFiles, mediaFilesRef, setMediaFiles] = useRefState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    CommForm.resetFields();
    return () => {
      setNewAudiences({
        users: [],
      });
      CommForm.resetFields();
    };
  }, []);

  /*
   * Check current form data from parent
   * Set old data to the form
   */
  /*   useEffect(() => {
    if (formData) {
      const hasValFormData = clean(formData);
      setIsDisabled(false);
    }
  }, [formData]); */

  const onFinish = values => {
    const { audienceSelect, media, ...restVal } = values;
    const resultVal = {
      audience: newAudiences,
      media: {
        type: 'file',
        url: mediaFiles[0]
      },
      ...restVal,
    };
    !!result && result(resultVal);
    !!nextStep && nextStep();
    CommForm.resetFields();
  };

  // return: { students: [object], users: [object], studentsID: ['_id'], usersID: ['_id'] }
  const onChangeAudienceSelect = useCallback(
    (values, options) => {
      let result = {
        users: [],
      };
      if (Array.isArray(options) && options.length > 0) {
        options?.map(option => {
          if (option?.group) {
            let arrIDs = result[`${option?.group}`] || [];
            let newArrIDs = arrIDs.concat([option.value]);
            result = {
              ...result,
              [option?.group]: newArrIDs,
            };
          }
        });
      }
      setNewAudiences(result);
    },
    [newAudiences],
  );

  const DEFAULT_REASONS = PARENT_COMM_REASON;
  const sortBy = 'label';
  const listReasons = DEFAULT_REASONS.map(elem => {
    return {
      key: elem.key,
      label: elem.label,
      title: elem.title || elem.label,
      value: elem.value,
    };
  });
  // sort options
  listReasons.sort(sortByKey(sortBy));

  const uploadMediaFiles = useCallback(data => { 
    setMediaFiles(data);
    setUploading(false);
  }, []);
  const fileOnRemove = file => {
    CommForm.setFieldsValue({
      media: [],
    });
    setMediaFiles([]);
  };
  const customItemRender = useCallback(
    (originNode, file) => {
      const initFile = mediaFiles?.length > 0 ? mediaFiles[0] : file;
      return (
        <PreviewMedia showTitle={false} file={initFile} remove={fileOnRemove} />
      );
    },
    [mediaFiles, fileOnRemove],
  );

  const getMediaField = useCallback(() => {
    return (
      <div className="communication-media-upload">
        <h3 className="media-title">L'attachement</h3>
        {mediaFiles && mediaFiles.length == 0 && (
          <div className={`proof-option-upload ${uploading ? 'loading' : ''}`}>
            <UploadMedia
              name="media"
              media={mediaFiles}
              setProgress={setUploading}
              uploadChange={uploadMediaFiles}
            />
          </div>
        )}
        {mediaFiles && mediaFiles.length > 0 && (
          <div className="form-proof">
            <Form.Item
              name="media"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              noStyle
              normalize={(value, prevValue, prevValues) => {
                if (value.length > 1) {
                  message.error(
                    `Vous ne pouvez télécharger qu'un seul fichier!`,
                  );
                  return false;
                }
              }}
            >
              <Upload
                multiple={false}
                maxCount={1}
                loading={uploading}
                fileList={mediaFiles}
                onRemove={fileOnRemove}
                itemRender={customItemRender}
                description="Téléchargement de documents (Scan, PDF, ...)"
              />
            </Form.Item>
          </div>
        )}
      </div>
    );
  }, [
    uploading,
    mediaFiles,
    fileOnRemove,
    setUploading,
    uploadMediaFiles,
    customItemRender,
  ]);

  return (
    <div className={childClassName}>
      <Form
        form={CommForm}
        className="form-communication__form"
        name="form_communication_reason"
        // initialValues={initFormData}
        onFinish={onFinish}
        onValuesChange={values => {
          setIsDisabled(false);
        }}
      >
        <CustomSelect
          className="accent-label"
          label="Motif : Autre requête"
          placeholder="Selectionner"
          name="reason"
          options={listReasons}
        />

        <DynamicMultipleSelect
          key="axios-select-multiple-modal"
          index="axios-select-multiple-modal"
          mode="new"
          method="new"
          onChange={onChangeAudienceSelect}
          className={classNames(
            'accent-label',
            'custom-form-column',
            'dynamic-multiple-select-column',
          )}
          data={{
            label: 'Audience',
            name: 'audienceSelect',
            endponit: `communications/audience/by/admins,teachers`,
            endponitSend: null,
            displayLabel: true,
            required: true,
            type: 'axios-select-multiple',
            hasModeView: true,
            col: 24,
            method: 'POST',
            groupped: true,
            optionLabelProp: 'title', // 'label
            value: [],
            className: 'axios-select',
            mode: 'multiple',
            isSearch: true,
            placeholder: 'Selectionner le(s) audience',
            showAsTag: true,
          }}
        />

        <TextField
          className="accent-label parent-comm-title"
          data={{
            displayLabel: true,
            required: true,
            label: 'Title',
            name: 'title',
          }}
        />

        <TextareaField
          className="accent-label"
          index={2}
          rows={12}
          data={{
            displayLabel: true,
            required: true,
            label: 'Message',
            name: 'message',
          }}
        />


        <div className="communication-media-upload">
          <h3 className="media-title">L'attachement</h3>
          {mediaFiles && mediaFiles.length == 0 && (
            <div className={`proof-option-upload ${uploading ? 'loading' : ''}`}>
              <UploadMedia
                name="media"
                media={mediaFiles}
                setProgress={setUploading}
                uploadChange={uploadMediaFiles}
              />
            </div>
          )}
          {mediaFiles && mediaFiles.length > 0 && (
            <div className="form-proof">
              <Form.Item
                name="media"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                noStyle
                normalize={(value, prevValue, prevValues) => {
                  if (value.length > 1) {
                    message.error(
                      `Vous ne pouvez télécharger qu'un seul fichier!`,
                    );
                    return false;
                  }
                }}
              >
                <Upload
                  multiple={false}
                  maxCount={1}
                  loading={uploading}
                  fileList={mediaFiles}
                  onRemove={fileOnRemove}
                  itemRender={customItemRender}
                  description="Téléchargement de documents (Scan, PDF, ...)"
                />
              </Form.Item>
            </div>
          )}
        </div>

        <div className="communication-actions-wrapper">
          {/* <Button
            className="btn-previous"
            onClick={() => prevStep !== undefined && prevStep()}
          >
            Précédent
          </Button> */}
          <Button
            className="next-btn"
            form="form_communication_reason"
            htmlType="submit"
            loading={loading}
            disabled={isDisabled || loading}
          >
            Envoyer
          </Button>
        </div>
      </Form>
    </div>
  );
}

RequestCommForm.defaultProps = {
  type: 'medical',
};

RequestCommForm.propTypes = {
  type: PropTypes.oneOf(['medical', 'no-medical', 'sickness']).isRequired,
  result: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
};

export default memo(RequestCommForm);
